import { useSelector } from 'react-redux';
import { selectJobId, selectNcFileId, selectPartId, selectProductionOrderId } from '@reducers/details';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { useHeaderConfigPluralCheck } from './useHeaderConfigPluralCheck';
import { selectSelectedItem } from '@reducers/machines';
import { Machine } from '@interfaces/machines';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { ImageDocumentReference } from '@v2/components/imageHandling/types/types';
import { DOCUMENT_ENTITY_TYPE, KEYS_TAGS, OP_TAG_NAMING } from '@components/imageHandling/common';
import { selectBatchId, selectSingleBatch } from '@reducers/batch';
import { ProductionBatchStatistics } from '@interfaces/search';
import { useGetHeaderConfig } from './useGetHeaderConfig';
import { injectConditionalArrayItems } from '@helpers';

interface UseBuildLinkListByHeaderConfigAndNavigationContextParams {
  selectedMachine?: {
    machineId: number;
    shortName: string;
  };
  shouldLinkOnlyMachine?: boolean;
}

export const useBuildLinkListByHeaderConfigAndNavigationContext = ({
  selectedMachine,
  shouldLinkOnlyMachine,
}: UseBuildLinkListByHeaderConfigAndNavigationContextParams = {}) => {
  const headerConfig: HeaderConfig = useGetHeaderConfig();
  const defaultSelectedMachine: Machine = useSelector(selectSelectedItem);
  const batchData: ProductionBatchStatistics | null = useSelector(selectSingleBatch);

  const machine = selectedMachine ?? defaultSelectedMachine;

  const { safeIsDetailsScreen, safeIsMachinesScreenCheck } = useNavigationCheck();

  const _batchIdFromReducer: number | null = useSelector(selectBatchId);
  const partIdFromReducer: number | null = useSelector(selectPartId);
  const jobIdFromReducer: number | null = useSelector(selectJobId);
  const ncFileIdFromReducer: number | null = useSelector(selectNcFileId);
  const productionOrderIdFromReducer: number | null = useSelector(selectProductionOrderId);

  const batchIdFromReducer = _batchIdFromReducer || batchData?.productionBatchId;

  const { batchPlural, jobsPlural, ncFilePlural, partPlural, productionOrdersPlural } = useHeaderConfigPluralCheck();

  const { batch, jobs, ncFile, part, productionOrders } = (() => {
    const defaultReturn = {
      part: { id: null, name: '' },
      jobs: [],
      productionOrders: [],
      ncFile: { id: null, name: '' },
      batch: { id: null, name: '' },
    };

    if (shouldLinkOnlyMachine) {
      return defaultReturn;
    }

    if (safeIsDetailsScreen) {
      return {
        part:
          partIdFromReducer != null
            ? {
                id: partIdFromReducer,
                name: headerConfig.part.name ?? '',
              }
            : null,
        jobs: jobIdFromReducer != null ? [{ id: jobIdFromReducer, name: String(headerConfig.job) ?? '' }] : null,
        ncFile: {
          id: ncFileIdFromReducer,
          name: headerConfig.ncFile,
        },
        productionOrders:
          productionOrderIdFromReducer != null ? [{ id: productionOrderIdFromReducer, name: String(headerConfig.productionOrder) ?? '' }] : [],
        batch: { id: batchIdFromReducer, name: String(headerConfig.batch) ?? '' },
      };
    }

    if (safeIsMachinesScreenCheck) {
      return {
        part: {
          id: defaultSelectedMachine?.machineStatus?.partDetails?.partId,
          name: defaultSelectedMachine?.machineStatus?.partDetails?.partName,
        },
        jobs:
          batchData?.links?.length > 0
            ? batchData?.links?.map((l) => ({
                id: l?.productionOperationId,
                name: `${l?.productionOrderNumber}-${l?.productionOperationNumber}`,
              }))
            : null,
        ncFile: { id: defaultSelectedMachine?.machineStatus?.ncFileId, name: defaultSelectedMachine?.machineStatus?.ncFile },
        productionOrders:
          batchData?.links?.length > 0
            ? [
                ...new Map(
                  batchData.links.map((l) => [
                    l.productionOrderId,
                    {
                      id: l.productionOrderId,
                      name: l.productionOrderNumber,
                    },
                  ])
                ).values(),
              ]
            : null,
        batch: { id: defaultSelectedMachine?.machineStatus?.productionBatchId, name: batchData?.batchNumber },
      };
    }

    return { part: { id: null, name: '' }, jobs: [], productionOrders: [], ncFile: { id: null, name: '' }, batch: { id: null, name: '' } };
  })();

  const { shouldAddPartLink, shouldAddBatchLink, shouldAddNcFileLink, shouldAddJobLinks, shouldAddProductionOrderLinks, shouldAddMachineLink } =
    (() => {
      const defaultReturn = {
        shouldAddPartLink: false,
        shouldAddJobLinks: false,
        shouldAddNcFileLink: false,
        shouldAddProductionOrderLinks: false,
        shouldAddBatchLink: false,
        shouldAddMachineLink: false,
      };

      if (shouldLinkOnlyMachine) {
        return { ...defaultReturn, shouldAddMachineLink: true };
      }

      if (safeIsDetailsScreen) {
        return {
          shouldAddPartLink: !!headerConfig.part && !partPlural && part?.id != null,
          shouldAddJobLinks: !!headerConfig.job && !jobsPlural && !!jobs && jobs?.length > 0,
          shouldAddNcFileLink: !!headerConfig.ncFile && !ncFilePlural && ncFile?.id != null,
          shouldAddProductionOrderLinks:
            !!headerConfig.productionOrder && !productionOrdersPlural && !!productionOrders && productionOrders?.length > 0,
          shouldAddBatchLink: !!headerConfig.batch && !batchPlural && batch?.id != null,
          shouldAddMachineLink: false,
        };
      }

      if (safeIsMachinesScreenCheck) {
        return {
          shouldAddPartLink: part?.id != null,
          shouldAddJobLinks: !!jobs && jobs?.length > 0,
          shouldAddNcFileLink: ncFile?.id != null,
          shouldAddProductionOrderLinks: !!productionOrders && productionOrders?.length > 0,
          shouldAddBatchLink: batch?.id != null,
          shouldAddMachineLink: true,
        };
      }

      return {
        shouldAddPartLink: false,
        shouldAddJobLinks: false,
        shouldAddNcFileLink: false,
        shouldAddProductionOrderLinks: false,
        shouldAddBatchLink: false,
        shouldAddMachineLink: false,
      };
    })();

  if (!safeIsMachinesScreenCheck && !safeIsDetailsScreen) return [];

  const links: ImageDocumentReference[] = [
    ...injectConditionalArrayItems(shouldAddPartLink, {
      scopeId: KEYS_TAGS.Part,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.Part],
      scopeReferenceId: part?.id,
      scopeReferenceName: part?.name,
    }),
    ...injectConditionalArrayItems(shouldAddBatchLink, {
      scopeId: KEYS_TAGS.Batch,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.Batch],
      scopeReferenceId: batch?.id,
      scopeReferenceName: batch?.name,
    }),
    ...injectConditionalArrayItems(shouldAddNcFileLink, {
      scopeId: KEYS_TAGS.NcFile,
      scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.NcFile],
      scopeReferenceId: ncFile?.id,
      scopeReferenceName: ncFile?.name,
    }),
    ...injectConditionalArrayItems(
      shouldAddJobLinks && !!jobs,
      jobs?.map((job) => ({
        scopeId: KEYS_TAGS.ProductionOperation,
        scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.ProductionOperation],
        scopeReferenceId: job?.id,
        scopeReferenceName: job?.name,
      }))
    ),
    ...injectConditionalArrayItems(
      shouldAddProductionOrderLinks && !!productionOrders,
      productionOrders?.map((productionOrder) => ({
        scopeId: KEYS_TAGS.ProductionOrder,
        scopeName: OP_TAG_NAMING[DOCUMENT_ENTITY_TYPE.ProductionOrder],
        scopeReferenceId: productionOrder?.id,
        scopeReferenceName: productionOrder?.name,
      }))
    ),
    ...injectConditionalArrayItems(shouldAddMachineLink, {
      scopeId: KEYS_TAGS.Machine,
      scopeName: 'Machine',
      scopeReferenceId: machine?.machineId,
      scopeReferenceName: machine.shortName ?? '',
    }),
  ];

  return links;
};
